import React, { useState, useEffect, useCallback } from 'react';
import { Role, Permission } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Crown, 
  Plus, 
  Edit, 
  Trash2, 
  Shield,
  Calendar,
  FileText,
  Users,
  Key,
  Settings
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function RolesRegistry() {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rights: [],
    is_system_role: false
  });

  // Debug state
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Debug effect for formData
  useEffect(() => {
    if (isEditDialogOpen) {
      console.log('FormData in edit dialog:', formData);
      console.log('FormData rights:', formData.rights);
      console.log('FormData rights length:', formData.rights.length);
      console.log('Permissions count:', permissions.length);
    }
  }, [formData, isEditDialogOpen, permissions.length]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [rolesData, permissionsData] = await Promise.all([
        Role.list(),
        Permission.list()
      ]);
      setRoles(rolesData.roles || []);
      setPermissions(permissionsData.permissions || []);
    } catch (err) {
      console.error('Error loading roles registry data:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRole = async () => {
    try {
      await Role.create(formData);
      setIsCreateDialogOpen(false);
      setFormData({ name: '', description: '', rights: [], is_system_role: false });
      await loadData();
    } catch (err) {
      console.error('Error creating role:', err);
      setError('Ошибка создания роли');
    }
  };

  const handleEditRole = async () => {
    try {
      await Role.update(selectedRole.id, formData);
      setIsEditDialogOpen(false);
      setSelectedRole(null);
      setFormData({ name: '', description: '', rights: [], is_system_role: false });
      await loadData();
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Ошибка обновления роли');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm('Вы уверены, что хотите удалить эту роль?')) {
      return;
    }
    try {
      await Role.delete(roleId);
      await loadData();
    } catch (err) {
      console.error('Error deleting role:', err);
      setError('Ошибка удаления роли');
    }
  };

  const openEditDialog = (role) => {
    console.log('Opening edit dialog for role:', role);
    console.log('Role rights:', role.rights);
    
    // Ensure rights is an array
    const rightsArray = Array.isArray(role.rights) ? role.rights : [];
    console.log('Rights array:', rightsArray);
    console.log('Rights array length:', rightsArray.length);
    
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      rights: rightsArray,
      is_system_role: role.is_system_role || false
    });
    setIsEditDialogOpen(true);
  };

  const handlePermissionToggle = useCallback((permissionId) => {
    console.log('Toggling permission:', permissionId);
    
    setFormData(prev => {
      const currentRights = prev.rights || [];
      const newRights = currentRights.includes(permissionId)
        ? currentRights.filter(id => id !== permissionId)
        : [...currentRights, permissionId];
      console.log('New rights:', newRights);
      console.log('New rights length:', newRights.length);
      return {
        ...prev,
        rights: newRights
      };
    });
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPermissionName = (permissionId) => {
    const permission = permissions.find(p => p.id === permissionId);
    return permission ? permission.name : 'Неизвестное право';
  };

  const groupPermissionsByResource = (permissionIds) => {
    const grouped = {};
    permissionIds.forEach(id => {
      const permission = permissions.find(p => p.id === id);
      if (permission) {
        const [resource] = permission.name.split('.');
        if (!grouped[resource]) {
          grouped[resource] = [];
        }
        grouped[resource].push(permission);
      }
    });
    return grouped;
  };

  if (isLoading) {
    return (
      <Card className="elegant-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-600" />
            Реестр ролей
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-slate-600">Загрузка...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="elegant-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-600" />
            Реестр ролей
          </CardTitle>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Добавить роль
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Создать роль</DialogTitle>
                <DialogDescription>
                  Заполните данные для создания новой роли
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Название роли</Label>
                  <Input
                    id="name"
                    placeholder="Название роли"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Описание</Label>
                  <Textarea
                    id="description"
                    placeholder="Описание назначения роли"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_system_role"
                    checked={formData.is_system_role}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_system_role: checked })}
                  />
                  <Label htmlFor="is_system_role">Системная роль</Label>
                </div>
                <div>
                  <Label>Права доступа</Label>
                  <div className="mt-2 max-h-60 overflow-y-auto border rounded-md p-4">
                    {permissions.map(permission => (
                      <div key={permission.id} className="flex items-center space-x-2 mb-2">
                        <Checkbox
                          id={`perm_${permission.id}`}
                          checked={formData.rights.includes(permission.id)}
                          onCheckedChange={() => handlePermissionToggle(permission.id)}
                        />
                        <Label htmlFor={`perm_${permission.id}`} className="text-sm">
                          {permission.name} - {permission.description}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Отмена
                </Button>
                <Button onClick={handleCreateRole}>
                  Создать
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Роль</TableHead>
                <TableHead>Тип</TableHead>
                <TableHead>Права</TableHead>
                <TableHead>Описание</TableHead>
                <TableHead>Дата создания</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <Crown className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="font-medium">{role.name}</div>
                        <div className="text-sm text-slate-500">ID: {role.id.slice(0, 8)}...</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={role.is_system_role ? "default" : "outline"}>
                      {role.is_system_role ? (
                        <>
                          <Settings className="w-3 h-3 mr-1" />
                          Системная
                        </>
                      ) : (
                        <>
                          <Users className="w-3 h-3 mr-1" />
                          Пользовательская
                        </>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-xs">
                      {role.rights && role.rights.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {role.rights.slice(0, 3).map(permissionId => (
                            <Badge key={permissionId} variant="secondary" className="text-xs">
                              <Key className="w-2 h-2 mr-1" />
                              {getPermissionName(permissionId)}
                            </Badge>
                          ))}
                          {role.rights.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{role.rights.length - 3} еще
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">Нет прав</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-xs">
                      {role.description ? (
                        <div className="flex items-start gap-1">
                          <FileText className="w-3 h-3 mt-0.5 text-slate-400" />
                          <span className="text-sm text-slate-600">{role.description}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">Нет описания</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Calendar className="w-3 h-3" />
                      {formatDate(role.created_date)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(role)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {!role.is_system_role && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRole(role.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Редактировать роль</DialogTitle>
            <DialogDescription>
              Измените данные роли
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Название роли</Label>
              <Input
                id="edit_name"
                placeholder="Название роли"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit_description">Описание</Label>
              <Textarea
                id="edit_description"
                placeholder="Описание назначения роли"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit_is_system_role"
                checked={formData.is_system_role}
                onCheckedChange={(checked) => setFormData({ ...formData, is_system_role: checked })}
              />
              <Label htmlFor="edit_is_system_role">Системная роль</Label>
            </div>
            <div>
              <Label>Права доступа</Label>
              <div className="mt-2 max-h-60 overflow-y-auto border rounded-md p-4">
                {permissions.map(permission => {
                  const isChecked = formData.rights.includes(permission.id);
                  return (
                    <div key={`${permission.id}-${isChecked}`} className="flex items-center space-x-2 mb-2">
                      <Checkbox
                        id={`edit_perm_${permission.id}`}
                        checked={isChecked}
                        onCheckedChange={() => handlePermissionToggle(permission.id)}
                      />
                      <Label htmlFor={`edit_perm_${permission.id}`} className="text-sm">
                        {permission.name} - {permission.description} ({isChecked ? 'checked' : 'unchecked'})
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleEditRole}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
